'use client';

import Link from 'next/link';
import { Package, Star } from 'lucide-react';
import type { ProductFamily } from '@/lib/data/software';

interface FamilyCardProps {
  family: ProductFamily;
}

// Tag color mappings
const tagColorClasses: Record<string, string> = {
  red: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  orange: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  green: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
  sky: 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300',
  blue: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  purple: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  pink: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300',
  gray: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
};

export default function FamilyCard({ family }: FamilyCardProps) {
  const displayTags = family.tags?.slice(0, 4) || [];
  const remainingTags = (family.tags?.length || 0) - displayTags.length;

  // Card is a <div> (not a wrapping <a>) with a stretched overlay link. This
  // keeps the tag links as valid, separate anchors — nesting <a> inside <a>
  // produces invalid HTML the browser auto-corrects, which diverges from the
  // React tree and trips a #418 hydration mismatch.
  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-blue-300 hover:shadow-lg transition-all duration-200">
      {/* Stretched link covers the whole card for navigation */}
      <Link
        href={`/software/${family.slug}`}
        aria-label={`View ${family.name} plans`}
        className="absolute inset-0 z-0"
      />

      {/* Card Header */}
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {family.iconUrl ? (
              <img
                src={family.iconUrl}
                alt={`${family.name} icon`}
                className="w-12 h-12 rounded-lg object-contain"
              />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {family.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{family.vendorName}</p>
            </div>
          </div>
          {family.featured && (
            <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-medium rounded-full">
              <Star className="w-3 h-3 fill-current" />
              Featured
            </span>
          )}
        </div>

        {/* Description - show first part of description, fallback to tagline */}
        {(family.description || family.tagline) && (
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            {family.description
              ? family.description.split('\n')[0].substring(0, 200)
              : family.tagline}
          </p>
        )}

        {/* Tags - sit above the stretched link so they stay independently clickable */}
        {displayTags.length > 0 && (
          <div className="relative z-10 mt-3 flex flex-wrap gap-1.5">
            {displayTags.map((tag) => (
              <Link
                key={tag.id}
                href={`/software/tag/${tag.slug}`}
                className={`inline-flex items-center min-h-[24px] px-2 py-0.5 text-xs font-medium rounded-full hover:opacity-80 transition-opacity ${tagColorClasses[tag.color] || tagColorClasses.gray}`}
              >
                {tag.name}
              </Link>
            ))}
            {remainingTags > 0 && (
              <span className="inline-flex items-center min-h-[24px] px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                +{remainingTags}
              </span>
            )}
          </div>
        )}

        {/* Variant Count */}
        {family.variantCount !== undefined && family.variantCount > 0 && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {family.variantCount} {family.variantCount === 1 ? 'plan' : 'plans'} available
          </p>
        )}
      </div>

      {/* Card Footer */}
      <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-800">
        <span className="text-sm font-medium text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300">
          View Plans &rarr;
        </span>
      </div>
    </div>
  );
}
